import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, tap } from 'rxjs';
import { ApiService } from '../api/api.service';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { STORAGE_KEYS } from '../constants/storage-keys';
import { toBase64 } from '../utils/base64.util';
import { FcmService } from './fcm.service';
import { ApiResponse } from '../models/api-response.model';
import {
  AuthSession,
  Company,
  LoginPayload,
  LoginRequestBody,
  LoginResponse,
  UserProfile,
  VerifyRequestBody,
  VerifyResponse,
} from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private api = inject(ApiService);
  private router = inject(Router);
  private fcm = inject(FcmService);

  private _user = signal<UserProfile | null>(this.readUserFromStorage());
  readonly user = computed(() => this._user());
  readonly isAuthenticated = computed(() => !!this._user() && !!this.accessToken);

  /**
   * Hak akses Master Data. Backend kadang kirim flag sbg angka `1` dan kadang
   * string `'1'`, jadi dibandingin lewat String() biar dua-duanya kebaca
   * (kalau strict `=== '1'`, akun dgn `1` numerik ke-hide dari nav — bug).
   * Dipakai bareng oleh masterDataGuard + dropdown topbar + flyout sidebar.
   */
  readonly canMasterData = computed(() => String(this._user()?.access_role?.master_data ?? '') === '1');

  /**
   * Staf support MCP (role JWT `super_admin`, sama dengan aturan backend `hasSupportRole`).
   * Tiket mereka kelola di Admin Panel terpisah, bukan di platform ini. JANGAN pakai
   * access_role.support — flag itu cuma visibilitas menu Support (bernilai "1" utk setiap owner).
   */
  readonly isSupportAgent = computed(() => this._user()?.role === 'super_admin');

  /** Companies returned by /auth2 — populated after a successful login call. */
  readonly companies = signal<Company[]>(this.readCompaniesFromStorage());

  /**
   * Kept in memory only (never written to localStorage) so the OTP screen
   * can trigger a resend by re-running step 1 without asking the user to
   * retype their password.
   */
  private pendingLogin: LoginPayload | null = null;

  /**
   * Guards against overlapping redirects when several requests fail with
   * 401/403 at once (including SSE streams that bypass the interceptor).
   */
  private sessionExpiring = false;

  get accessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.accessToken);
  }

  get sessionId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.authSessionId);
  }

  /**
   * Step 1 — POST /auth2
   * Encodes credentials to base64, attaches the Cloudflare Turnstile token
   * and the FCM device token, then stores the returned session id + company
   * list so the OTP screen can proceed to verification.
   */
  login(payload: LoginPayload, cfToken: string): Observable<LoginResponse> {
    return new Observable<LoginResponse>((subscriber) => {
      this.fcm.getDeviceToken().then((fcmToken) => {
        const body: LoginRequestBody = {
          user: toBase64(payload.email),
          password: toBase64(payload.password),
          verify: payload.verify,
          cftoken: cfToken,
          fcmToken,
        };

        this.api.post<LoginResponse>(API_ENDPOINTS.auth.login, body).subscribe({
          next: (res) => {
            if (res.data) localStorage.setItem(STORAGE_KEYS.authSessionId, res.data);
            localStorage.setItem(STORAGE_KEYS.authEmail, payload.email);
            localStorage.setItem(STORAGE_KEYS.authChannel, payload.verify);
            this.pendingLogin = payload;
            const companies = res.companies ?? [];
            localStorage.setItem(STORAGE_KEYS.authCompanies, JSON.stringify(companies));
            this.companies.set(companies);
            subscriber.next(res);
            subscriber.complete();
          },
          error: (err) => subscriber.error(err),
        });
      });
    });
  }

  /** Step 2 — POST /auth2/verify */
  verifyOtp(code: string, ownerCode: string): Observable<AuthSession> {
    const body: VerifyRequestBody = {
      session: this.sessionId ?? '',
      code,
      owner_code: ownerCode,
    };

    return this.api.post<VerifyResponse>(API_ENDPOINTS.auth.verifyOtp, body).pipe(
      map((res) => {
        const { access_token, token, refresh_token, ...profile } = res.data;
        return {
          accessToken: access_token ?? token,
          refreshToken: refresh_token,
          user: profile as UserProfile,
        };
      }),
      tap((session) => this.persistSession(session))
    );
  }

  /** Re-runs step 1 with the last-used credentials/channel to issue a new OTP. */
  resendOtp(cfToken: string): Observable<LoginResponse> {
    if (!this.pendingLogin) {
      throw new Error('No pending login to resend. Please submit the login form again.');
    }
    return this.login(this.pendingLogin, cfToken);
  }

  /** Manual logout (user clicked "Log out" in the topbar). */
  logout(): void {
    this.clearSession();
    this.router.navigateByUrl('/auth/login');
  }

  /**
   * Ganti password akun — POST /password dengan body { current, password }.
   * Sengaja tidak menyentuh session di sini; komponen yang memutuskan untuk
   * memanggil {@link forceLogoutAfterPasswordChange} setelah sukses supaya
   * user login ulang memakai password baru.
   */
  changePassword(currentPassword: string, newPassword: string): Observable<ApiResponse<unknown>> {
    const body = { current: currentPassword, password: newPassword };
    return this.api.post<ApiResponse<unknown>>(API_ENDPOINTS.auth.changePassword, body).pipe(
      map((res) => {
        if (!res.success) {
          throw new Error(res.msg || 'Gagal mengubah password.');
        }
        return res;
      })
    );
  }

  /**
   * Dipanggil setelah ganti password sukses: buang SELURUH localStorage
   * (bukan cuma key auth), reset state in-memory, lalu arahkan ke halaman login.
   */
  forceLogoutAfterPasswordChange(): void {
    localStorage.clear();
    this.pendingLogin = null;
    this._user.set(null);
    this.companies.set([]);
    this.router.navigateByUrl('/auth/login');
  }

  /**
   * Generate QR TOTP untuk registrasi Google Authenticator — GET /auth2/totp/register.
   * Response `data` berupa data URI SVG (`data:image/svg+xml;base64,...`) yang
   * langsung bisa dipasang ke <img [src]>.
   */
  registerAuthenticator(): Observable<string> {
    return this.api.get<ApiResponse<string>>(API_ENDPOINTS.auth.registerAuth).pipe(
      map((res) => {
        if (!res.success || !res.data) {
          throw new Error(res.msg || 'Gagal membuat QR code authenticator.');
        }
        return res.data;
      })
    );
  }

  /**
   * Verifikasi kode 6-digit dari aplikasi authenticator untuk menyelesaikan
   * registrasi TOTP — POST /auth2/totp/registerverify dengan body { code }.
   */
  verifyRegisterAuthenticator(code: string): Observable<ApiResponse<unknown>> {
    return this.api.post<ApiResponse<unknown>>(API_ENDPOINTS.auth.verifyAuth, { code }).pipe(
      map((res) => {
        if (!res.success) {
          throw new Error(res.msg || 'Kode verifikasi tidak valid.');
        }
        return res;
      })
    );
  }

  /**
   * Session expired or revoked server-side (401/403). Clears all auth state and
   * redirects to login. Called from the HttpClient interceptor AND from the SSE
   * services that use raw `fetch` (which bypasses the interceptor). Coalesces
   * parallel failures so only the first call navigates — no redirect bouncing.
   */
  handleSessionExpired(): void {
    if (this.sessionExpiring) return;
    this.sessionExpiring = true;
    this.clearSession();
    this.router
      .navigateByUrl('/auth/login')
      .finally(() => { this.sessionExpiring = false; });
  }

  /** Clears persisted session + in-memory auth signals (no navigation). */
  private clearSession(): void {
    localStorage.removeItem(STORAGE_KEYS.accessToken);
    localStorage.removeItem(STORAGE_KEYS.refreshToken);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.authSessionId);
    localStorage.removeItem(STORAGE_KEYS.authCompanies);
    this._user.set(null);
    this.companies.set([]);
  }

  private persistSession(session: AuthSession): void {
    localStorage.setItem(STORAGE_KEYS.accessToken, session.accessToken);
    localStorage.setItem(STORAGE_KEYS.refreshToken, session.refreshToken);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(session.user));
    localStorage.removeItem(STORAGE_KEYS.authSessionId);
    localStorage.removeItem(STORAGE_KEYS.authCompanies);
    this.pendingLogin = null;
    this._user.set(session.user);
  }

  private readUserFromStorage(): UserProfile | null {
    const raw = localStorage.getItem(STORAGE_KEYS.user);
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  }

  private readCompaniesFromStorage(): Company[] {
    const raw = localStorage.getItem(STORAGE_KEYS.authCompanies);
    return raw ? (JSON.parse(raw) as Company[]) : [];
  }

  public getProfile(): UserProfile | null {
    return this.readUserFromStorage();
  }

  /**
   * Update path foto profile pada state global (`user`) supaya semua komponen
   * yang membaca `auth.user` — mis. topbar & halaman My Profile — ikut ter-refresh.
   *
   * @param photoPath URL / path foto baru (atau blob URL untuk preview).
   * @param persist   Simpan ke localStorage juga. Dibuat `false` saat preview
   *                  optimistic (blob URL) supaya tidak mengotori storage, dan
   *                  `true` setelah upload benar-benar sukses.
   */
  setProfilePhoto(photoPath: UserProfile['profile_photo_path'], persist = true): void {
    this._user.update((current) => {
      if (!current) return current;
      const updated: UserProfile = { ...current, profile_photo_path: photoPath };
      if (persist) {
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(updated));
      }
      return updated;
    });
  }
}
